import { forwardRef } from 'react';
import { BaseButton, type BaseButtonProps } from '@/components/common/base-button';
import clsx from 'clsx';
import styles from './styles.module.scss';
import { getHSize, getSize } from '@/utils/size';
import useBaseSize from '@/hooks/useBaseSize';
import { BuildingNameType } from '@/enums/building';

interface KeyboardButtonProps extends Omit<BaseButtonProps, 'title'> {
    title: string | React.ReactNode;
    width: string;
    height: string;
    mode: 'ko' | 'en';
    buildingType?: BuildingNameType;
    keyboardType: 'normal' | 'delete' | 'shift' | 'space' | 'search' | 'mode';
    active?: boolean;
    isNumber?: boolean;
}

const KeyboardButton = forwardRef<HTMLButtonElement, KeyboardButtonProps>(
    ({ title, width, height, keyboardType, active, className, mode, buildingType, isNumber, ...props }, ref) => {
        const btnClassname = clsx(
            styles['keyboard-button'],
            {
                [styles['keyboard-button__normal']]: keyboardType === 'normal',
                [styles['keyboard-button__delete']]: keyboardType === 'delete',
                [styles['keyboard-button__shift']]: keyboardType === 'shift',
                [styles['keyboard-button__space']]: keyboardType === 'space',
                [styles['keyboard-button__search']]: keyboardType === 'search',
                [styles['keyboard-button__search__active']]: active && keyboardType === 'search',
                [styles['keyboard-button__mode']]: keyboardType === 'mode',
                [styles['keyboard-button__shift__active']]: active && keyboardType === 'shift',
                [styles['keyboard-button__en']]: mode === 'en',
                [styles['keyboard-button__ko']]: mode === 'ko',
                // Font-family logic based on buildingType and isNumber
                [styles['keyboard-button__number-mall']]: isNumber && buildingType === BuildingNameType.MALL,
                [styles['keyboard-button__number-non-mall']]: isNumber && buildingType !== BuildingNameType.MALL,
                [styles['keyboard-button__number']]: isNumber && !buildingType, // Fallback for when buildingType is not provided
            },
            className
        );

        const { baseWidth, baseHeight } = useBaseSize();

        return (
            <BaseButton
                ref={ref}
                {...props}
                style={{ width: getSize(Number(width), baseWidth), height: getHSize(Number(height), baseHeight) }}
                className={btnClassname}
            >
                <span className={(isNumber || mode === 'en') ? 'number' : ''}>{title}</span>
            </BaseButton>
        );
    }
);

KeyboardButton.displayName = 'KeyboardButton';

export default KeyboardButton;
